import { useContext, useEffect, useRef } from 'react';
import { TokenContext } from '../Context/TokenContext';

/**
 * Connexion WebSocket directe et indépendante vers le ticket-service
 * (protocole {event, data, timestamp} propre à ce service, différent de
 * celui du WebSocketContext.js utilisé pour le reste du dashboard).
 * Purement un confort de rafraîchissement live : les callbacks sont
 * best-effort, la REST API reste la source de vérité.
 */
export function useTicketSocket({ onTicketCreated, onMessageCreated } = {}) {
    const { accessToken } = useContext(TokenContext);
    const wsRef = useRef(null);
    const retryRef = useRef(0);
    const callbacksRef = useRef({ onTicketCreated, onMessageCreated });
    callbacksRef.current = { onTicketCreated, onMessageCreated };

    useEffect(() => {
        if (!accessToken) return undefined;

        let closedByEffect = false;
        let retryTimeout;

        const connect = () => {
            const base = process.env.REACT_APP_TICKET_WS_URL || 'ws://localhost:3033/ws';
            let socket;
            try {
                socket = new WebSocket(`${base}?token=${encodeURIComponent(accessToken)}`);
            } catch (e) {
                return;
            }
            wsRef.current = socket;

            socket.onmessage = (event) => {
                try {
                    const msg = JSON.parse(event.data);
                    if (msg.event === 'ticket.created') {
                        callbacksRef.current.onTicketCreated?.(msg.data);
                    } else if (msg.event === 'message.created') {
                        callbacksRef.current.onMessageCreated?.(msg.data);
                    }
                } catch {
                    // message non-JSON ignoré
                }
            };

            socket.onclose = () => {
                if (closedByEffect) return;
                retryRef.current = Math.min(retryRef.current + 1, 5);
                const delay = retryRef.current * 2000;
                retryTimeout = setTimeout(connect, delay);
            };

            socket.onerror = () => {
                socket.close();
            };
        };

        connect();

        return () => {
            closedByEffect = true;
            clearTimeout(retryTimeout);
            wsRef.current?.close();
        };
    }, [accessToken]);
}
